import React from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { formatPrice } from '../../../utils/helpers';

const shortMXN = (v) => {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(v >= 10_000 ? 0 : 1)}k`;
  return `$${Math.round(v)}`;
};

const fmtDay = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', timeZone: 'UTC' });
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-xl border border-white/10 bg-gray-950/95 backdrop-blur px-4 py-3 shadow-2xl">
      <p className="text-[11px] uppercase tracking-wider text-gray-400">{fmtDay(label)}</p>
      <p className="text-lg font-bold text-white tabular-nums">{formatPrice(p.revenue)}</p>
      <p className="text-xs text-gray-400">{p.orders} pedido{p.orders === 1 ? '' : 's'}</p>
    </div>
  );
};

export const RevenueChart = ({ series, days, onDaysChange, total }) => (
  <div className="rounded-2xl border border-white/[0.06] bg-gray-900 p-6 shadow-[0_8px_30px_rgba(0,0,0,0.35)]" data-testid="revenue-chart">
    <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
      <div>
        <p className="text-[11px] uppercase tracking-[0.14em] text-gray-400 font-semibold">Ingreso bruto</p>
        <p className="text-2xl font-bold text-white tabular-nums mt-1" data-testid="revenue-chart-total">{formatPrice(total)}</p>
        <p className="text-xs text-gray-500 mt-0.5">Últimos {days} días · sin pedidos cancelados</p>
      </div>
      <div className="flex rounded-full bg-white/5 p-1 ring-1 ring-white/10">
        {[7, 30].map(d => (
          <button key={d} onClick={() => onDaysChange(d)} data-testid={`revenue-range-${d}`}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${days === d ? 'bg-orange-500 text-white' : 'text-gray-400 hover:text-white'}`}>
            {d} días
          </button>
        ))}
      </div>
    </div>
    <div className="h-72">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={series} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#f97316" stopOpacity={0.45} />
              <stop offset="100%" stopColor="#f97316" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
          <XAxis dataKey="date" tickFormatter={fmtDay} tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={false} tickLine={false}
            minTickGap={24} />
          <YAxis tickFormatter={shortMXN} tick={{ fill: '#6b7280', fontSize: 11 }} axisLine={false} tickLine={false} width={56} allowDecimals={false} />
          <Tooltip content={<ChartTooltip />} cursor={{ stroke: 'rgba(249,115,22,0.4)', strokeDasharray: '4 4' }} />
          <Area type="monotone" dataKey="revenue" stroke="#fb923c" strokeWidth={2.5} fill="url(#revFill)"
            activeDot={{ r: 5, fill: '#fb923c', stroke: '#111827', strokeWidth: 2 }} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  </div>
);
